import InputGroup from '../../components/basic_calculator/InputGroup.js';
import { useState } from 'react';
import { Navigation } from '../../components/website/navigation.jsx';
import { Piechart } from '../../components/basic_calculator/Piechart.jsx';
import JsonData from '../../data/website_data.json';
import ParamData from '../../data/calculator_parameters.json';
import ImageSlider from '../../components/basic_calculator/ImageSlider/ImageSlider.js';

function ResetButton({ resetDefault }) {
  return (
    <button
      className="btn-primary btn-lg font-nunito"
      type="button"
      onClick={resetDefault}
    >
      Reset to Default
    </button>
  );
}

// define the parameters
const initialElectricParams = ParamData.ElectricParams;
const initialDieselParams = ParamData.DieselParams;
const electricTruckModelInfo = ParamData.ElectricTruckModelInfo.filter(truck => truck.class === 'Heavy Duty');
const dieselTruckModelInfo = ParamData.DieselTruckModelInfo;

// Interactive interface here
function BasicCalculator() {
  const [electricParams, setElectricParams] = useState(initialElectricParams);
  const [dieselParams, setDieselParams] = useState(initialDieselParams);
  const [ownChargers, setOwnChargers] = useState('yes');
  const [defaultElectricParams, setDefaultElectricParams] = useState(initialElectricParams);
  const [defaultDieselParams, setDefaultDieselParams] = useState(initialDieselParams);

  const [selectedElectricBrand, setSelectedElectricBrand] = useState(electricTruckModelInfo[0].brand);
  const [selectedElectricMake, setSelectedElectricMake] = useState(electricTruckModelInfo[0].make);
  const [selectedElectricTruck, setSelectedElectricTruck] = useState(electricTruckModelInfo[0]);

  const [selectedDieselBrand, setSelectedDieselBrand] = useState(dieselTruckModelInfo[0].brand);
  const [selectedDieselMake, setSelectedDieselMake] = useState(dieselTruckModelInfo[0].make);
  const [selectedDieselTruck, setSelectedDieselTruck] = useState(dieselTruckModelInfo[0]);

  const [financialExpanded, setFinancialExpanded] = useState(true);
  const [operationalExpanded, setOperationalExpanded] = useState(true);

  const [footerCollapsed, setFooterCollapsed] = useState(false);

  const toggleFinancial = () => {
    setFinancialExpanded(!financialExpanded);
  };

  const toggleOperational = () => {
    setOperationalExpanded(!operationalExpanded);
  };

  const electric = electricParams.reduce((acc, param) => {
    acc[param.name] = param.cost;
    return acc;
  }, {});

  const {
    n_EV,
    evMSRP,
    evIncentives,
    evRetailTax,
    evDownPayment,
    evLoanPeriodYear,
    evLoanInterestYear,
    evHoldingPeriod,
    evResidualValue,
    evInsurance,
    evRegistrationFee,
    evAnnualMileage,
    evUnitOMCost,
    kwhpm,
    electricityCost,
    unitChargerCost,
    unitChargerIncentive,
    n_c,
    demandCharge,
    peakLoad,
    thirdPartyFee,
  } = electric;

  const diesel = dieselParams.reduce((acc, param) => {
    acc[param.name] = param.cost;
    return acc;
  }, {});

  const {
    n_d,
    dieselMSRP,
    dieselIncentives,
    dieselRetailTax,
    dieselDownPayment,
    dieselLoanPeriodYear,
    dieselLoanInterestYear,
    dieselHoldingPeriod,
    dieselResidualValue,
    dieselInsurance,
    dieselRegistrationFee,
    dieselAnnualMileage,
    dieselUnitOMCost,
    mpg,
    dieselCost,
    dieselNonZEVcostAnnual,
  } = diesel;

  // additional parameters that need to be defined
  const evPurchaseCost = (evMSRP - evIncentives) * (1 + evRetailTax / 100);
  const evLoanAmount = evPurchaseCost - evDownPayment;
  const evLoanPeriodMonth = evLoanPeriodYear * 12;
  const evLoanInterestMonth = evLoanInterestYear / 12;
  const evMonthlyPayment = evLoanAmount / (((1 + evLoanInterestMonth / 100) ** evLoanPeriodMonth - 1) / (evLoanInterestMonth / 100 * (1 + evLoanInterestMonth / 100) ** evLoanPeriodMonth));

  const dieselPurchaseCost = (dieselMSRP - dieselIncentives) * (1 + dieselRetailTax / 100);
  const dieselLoanAmount = dieselPurchaseCost - dieselDownPayment;
  const dieselLoanPeriodMonth = dieselLoanPeriodYear * 12;
  const dieselLoanInterestMonth = dieselLoanInterestYear / 12;
  const dieselMonthlyPayment = dieselLoanAmount / (((1 + dieselLoanInterestMonth / 100) ** dieselLoanPeriodMonth - 1) / (dieselLoanInterestMonth / 100 * (1 + dieselLoanInterestMonth / 100) ** dieselLoanPeriodMonth));

  // calculate the TCO
  function calculateVehicleCost(vehicleType) {
    if (vehicleType === 'electric') {
      const vehicleCost = ((evMSRP - evIncentives) * (1 + (evRetailTax / 100)) - (evMSRP * evResidualValue / 100)) * n_EV;
      return vehicleCost;
    } else if (vehicleType === 'diesel') {
      const vehicleCost = ((dieselMSRP - dieselIncentives) * (1 + (dieselRetailTax / 100)) - (dieselMSRP * dieselResidualValue / 100)) * n_d;
      return vehicleCost;
    }
  }

  function calculateMaintenanceCost(vehicleType) {
    if (vehicleType === 'electric') {
      const maintenanceCost = evHoldingPeriod * evAnnualMileage * evUnitOMCost;
      return maintenanceCost;
    } else if (vehicleType === 'diesel') {
      const maintenanceCost = dieselHoldingPeriod * dieselAnnualMileage * dieselUnitOMCost;
      return maintenanceCost;
    }
  }

  function calculateInsuranceCost(vehicleType) {
    if (vehicleType === 'electric') {
      const insuranceCost = (evInsurance + evRegistrationFee) * evHoldingPeriod;
      return insuranceCost;
    } else if (vehicleType === 'diesel') {
      const insuranceCost = (dieselInsurance + dieselRegistrationFee) * dieselHoldingPeriod;
      return insuranceCost;
    }
  }

  function calculateChargingCost(own) {
    if (own === 'yes') {
      const chargingCost = (unitChargerCost - unitChargerIncentive) * n_c + ((electricityCost * kwhpm * evAnnualMileage) + (demandCharge * peakLoad * 12)) * evHoldingPeriod;
      return chargingCost;
    } else {
      const chargingCost = thirdPartyFee * kwhpm * evAnnualMileage * evHoldingPeriod;
      return chargingCost;
    }
  }

  function calculateFinanceCost(vehicleType) {
    if (vehicleType === 'electric') {
      const financeCost = evMonthlyPayment * evLoanPeriodMonth - evLoanAmount;
      return financeCost;
    } else if (vehicleType === 'diesel') {
      const financeCost = dieselMonthlyPayment * dieselLoanPeriodMonth - dieselLoanAmount;
      return financeCost;
    }
  }

  function calculateFuelCost() {
    const fuel = dieselHoldingPeriod * dieselAnnualMileage / mpg * dieselCost;
    return fuel;
  }

  function calculateDieselNonZEVCost() {
    const dieselNonZEVcost = dieselNonZEVcostAnnual * dieselHoldingPeriod;
    return dieselNonZEVcost;
  }

  function calculateTCO(vehicleType, own) {
    // calculate TCO    
    const vehicleCost = calculateVehicleCost(vehicleType);
    const financeCost = calculateFinanceCost(vehicleType);
    const maintenanceCost = calculateMaintenanceCost(vehicleType);
    const insuranceCost = calculateInsuranceCost(vehicleType);

    let TCO;

    if (vehicleType === 'electric') {
      const chargingCost = calculateChargingCost(own);
      TCO = vehicleCost + financeCost + maintenanceCost + insuranceCost + chargingCost;
    } else if (vehicleType === 'diesel') {
      const fuelCost = calculateFuelCost();
      const dieselNonZEVcost = calculateDieselNonZEVCost();
      TCO = vehicleCost + financeCost + maintenanceCost + insuranceCost + fuelCost + dieselNonZEVcost;
    }

    // Ensure TCO is not NaN
    return isNaN(TCO) ? 0 : TCO;
  }

  function changeDefaultValue(truckNumber, isElectric = true) {
    const params = isElectric ? initialElectricParams : initialDieselParams;

    // Find the selected truck model from the model info
    const selectedTruck = isElectric
      ? electricTruckModelInfo.find(truck => truck.id === truckNumber)
      : dieselTruckModelInfo.find(truck => truck.id === truckNumber);

    if (!selectedTruck) return; // Exit if truck not found

    // First, update the MSRP using the value from the truck model
    const updatedMSRPParams = params.map(param => {
      if (param.name === (isElectric ? 'evMSRP' : 'dieselMSRP')) {
        return { ...param, cost: selectedTruck.msrp, default: selectedTruck.msrp };
      }
      return param;
    });

    // Then, update the Down Payment based on the adjusted MSRP (MSRP - Incentives + Tax)
    const finalUpdatedParams = updatedMSRPParams.map(param => {
      if (param.name === (isElectric ? 'evDownPayment' : 'dieselDownPayment')) {
        const msrpParam = updatedMSRPParams.find(p => p.name === (isElectric ? 'evMSRP' : 'dieselMSRP'));
        const incentivesParam = updatedMSRPParams.find(p => p.name === (isElectric ? 'evIncentives' : 'dieselIncentives'));
        const taxRateParam = updatedMSRPParams.find(p => p.name === (isElectric ? 'evRetailTax' : 'dieselRetailTax'));

        const msrpValue = msrpParam ? msrpParam.cost : 0;
        const incentivesValue = incentivesParam ? incentivesParam.cost : 0;
        const taxRateValue = taxRateParam ? taxRateParam.cost : 0;

        const adjustedMSRP = (msrpValue - incentivesValue) * (1 + taxRateValue / 100);
        const downPaymentValue = adjustedMSRP * 0.2; // 20% of adjusted MSRP
        return { ...param, cost: downPaymentValue, default: downPaymentValue };
      }
      return param;
    });

    if (isElectric) {
      setDefaultElectricParams(finalUpdatedParams);
      setElectricParams(finalUpdatedParams);
    } else {
      setDefaultDieselParams(finalUpdatedParams);
      setDieselParams(finalUpdatedParams);
    }
  }

  const handleElectricBrandChange = (e) => {
    const brand = e.target.value;
    setSelectedElectricBrand(brand);

    // Automatically select the first make associated with the selected brand
    const make = electricTruckModelInfo.find(truck => truck.brand === brand).make;
    setSelectedElectricMake(make);

    // Update the selected truck
    const selectedTruck = electricTruckModelInfo.find(truck => truck.brand === brand && truck.make === make);
    setSelectedElectricTruck(selectedTruck);
    onTruckChange(selectedTruck.id, true); // Update parameters based on selected truck
  };

  const handleElectricMakeChange = (e) => {
    const make = e.target.value;
    setSelectedElectricMake(make);

    // Update the selected truck
    const selectedTruck = electricTruckModelInfo.find(truck => truck.brand === selectedElectricBrand && truck.make === make);
    setSelectedElectricTruck(selectedTruck);
    onTruckChange(selectedTruck.id, true); // Update parameters based on selected truck
  };

  const handleDieselBrandChange = (e) => {
    const brand = e.target.value;
    setSelectedDieselBrand(brand);

    // Automatically select the first make associated with the selected brand
    const make = dieselTruckModelInfo.find(truck => truck.brand === brand).make;
    setSelectedDieselMake(make);

    // Update the selected truck
    const selectedTruck = dieselTruckModelInfo.find(truck => truck.brand === brand && truck.make === make);
    setSelectedDieselTruck(selectedTruck);
    onTruckChange(selectedTruck.id, false); // Update parameters based on selected truck
  };

  const handleDieselMakeChange = (e) => {
    const make = e.target.value;
    setSelectedDieselMake(make);

    // Update the selected truck
    const selectedTruck = dieselTruckModelInfo.find(truck => truck.brand === selectedDieselBrand && truck.make === make);
    setSelectedDieselTruck(selectedTruck);
    onTruckChange(selectedTruck.id, false); // Update parameters based on selected truck
  };

  const onChargerChange = e => {
    setOwnChargers(e.target.value);
  }

  const onTruckChange = (truckValue, isElectric = true) => {
    const selectedTruck = isElectric
      ? electricTruckModelInfo.find(truck => truck.id === truckValue)
      : dieselTruckModelInfo.find(truck => truck.id === truckValue);

    if (isElectric) {
      setSelectedElectricBrand(selectedTruck.brand);
      setSelectedElectricMake(selectedTruck.make);
      setSelectedElectricTruck(selectedTruck);
    } else {
      setSelectedDieselBrand(selectedTruck.brand);
      setSelectedDieselMake(selectedTruck.make);
      setSelectedDieselTruck(selectedTruck);
    }

    changeDefaultValue(truckValue, isElectric);
  };

  const resetToDefault = () => {
    setElectricParams(defaultElectricParams.map(item => ({ ...item, cost: item.default })));
    setDieselParams(defaultDieselParams.map(item => ({ ...item, cost: item.default })));
  };

  const handleParamChange = (vehicleType, name, value) => {
    let input = value === '' || isNaN(parseFloat(value)) ? value : parseFloat(value);

    if (vehicleType === 'electric') {
      setElectricParams((prevParams) => {
        // First, update the specific parameter value
        const updatedParams = prevParams.map((param) => {
          return param.name === name ? { ...param, cost: input } : param;
        });

        // Then, update the down payment based on the new values
        return updatedParams.map((param) => {
          if (param.name === 'evDownPayment' && (name === 'evMSRP' || name === 'evIncentives' || name === 'evRetailTax')) {
            const msrpParam = updatedParams.find(p => p.name === 'evMSRP');
            const incentivesParam = updatedParams.find(p => p.name === 'evIncentives');
            const taxRateParam = updatedParams.find(p => p.name === 'evRetailTax');

            const msrpValue = msrpParam ? msrpParam.cost : 0;
            const incentivesValue = incentivesParam ? incentivesParam.cost : 0;
            const taxRateValue = taxRateParam ? taxRateParam.cost : 0;

            const adjustedMSRP = (msrpValue - incentivesValue) * (1 + taxRateValue / 100);
            let downPaymentValue = adjustedMSRP * 0.2; // 20% of adjusted MSRP
            downPaymentValue = Math.floor(downPaymentValue * 100) / 100;
            downPaymentValue = downPaymentValue < 0 ? 0 : downPaymentValue;

            return { ...param, cost: downPaymentValue };
          }
          return param;
        });
      });
    } else if (vehicleType === 'diesel') {
      setDieselParams((prevParams) => {
        // First, update the specific parameter value
        const updatedParams = prevParams.map((param) => {
          return param.name === name ? { ...param, cost: input } : param;
        });

        // Then, update the down payment based on the new values
        return updatedParams.map((param) => {
          if (param.name === 'dieselDownPayment' && (name === 'dieselMSRP' || name === 'dieselIncentives' || name === 'dieselRetailTax')) {
            const msrpParam = updatedParams.find(p => p.name === 'dieselMSRP');
            const incentivesParam = updatedParams.find(p => p.name === 'dieselIncentives');
            const taxRateParam = updatedParams.find(p => p.name === 'dieselRetailTax');

            const msrpValue = msrpParam ? msrpParam.cost : 0;
            const incentivesValue = incentivesParam ? incentivesParam.cost : 0;
            const taxRateValue = taxRateParam ? taxRateParam.cost : 0;

            const adjustedMSRP = (msrpValue - incentivesValue) * (1 + taxRateValue / 100);
            let downPaymentValue = adjustedMSRP * 0.2; // 20% of adjusted MSRP
            downPaymentValue = Math.floor(downPaymentValue * 100) / 100;
            downPaymentValue = downPaymentValue < 0 ? 0 : downPaymentValue;

            return { ...param, cost: downPaymentValue };
          }
          return param;
        });
      });
    }
  };

  const uniqueElectricBrands = [...new Set(electricTruckModelInfo.map(truck => truck.brand))];
  const uniqueDieselBrands = [...new Set(dieselTruckModelInfo.map(truck => truck.brand))];

  // Piechart objects
  const eProps = {
    "Vehicle + Tax": Math.round(calculateVehicleCost('electric')),
    "Financing Cost": Math.round(calculateFinanceCost('electric')),
    "Maintenance": Math.round(calculateMaintenanceCost('electric')),
    "Insurance": Math.round(calculateInsuranceCost('electric')),
    "Charging": Math.round(calculateChargingCost(ownChargers)),
  };

  const dProps = {
    "Vehicle + Tax": Math.round(calculateVehicleCost('diesel')),
    "Financing Cost": Math.round(calculateFinanceCost('diesel')),
    "Maintenance": Math.round(calculateMaintenanceCost('diesel')),
    "Insurance": Math.round(calculateInsuranceCost('diesel')),
    "Fuel": Math.round(calculateFuelCost()),
    "Non ZEV Cost": Math.round(calculateDieselNonZEVCost()),
  };

  const evMsrpParam = electricParams.find((param) => param.name === "evMSRP");
  const evMsrpCost = evMsrpParam ? evMsrpParam.cost : null;

  return (
    <div className='bg-primary-backgroundGray'>
      <Navigation />
      <div className="p-4 sm:px-6 lg:px-8 py-8">
        <div id="instructions" className="bg-white rounded-lg shadow-sm p-12 lg:p-16 mt-24 mb-12">
          <h3 className="text-xl font-bold text-center text-gray-800 mb-4 font-nunito">Curious how an electric truck and diesel truck compare one-to-one?</h3>
          <p className="text-lg text-gray-600 font-nunito text-center">
            Fill in the fields below and we'll compare the costs for you.
          </p>
        </div>
        <div className="container mx-auto px-4 pt-4 mt-16">
          {/* Model Selection Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Electric Truck Section */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-xl font-nunito font-bold text-center text-gray-800 mb-4">Electric Truck</h3>
              <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-3 items-center mb-4">
                <label className="text-sm font-nunito font-medium text-gray-700 text-left">Make:</label>
                <select
                  className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-nunito"
                  value={selectedElectricBrand}
                  onChange={handleElectricBrandChange}
                >
                  {uniqueElectricBrands.map((brand, idx) => (
                    <option key={idx} value={brand}>{brand}</option>
                  ))}
                </select>

                <label className="text-sm font-nunito font-medium text-gray-700 text-left">Model:</label>
                <select
                  className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-nunito"
                  value={selectedElectricMake}
                  onChange={handleElectricMakeChange}
                >
                  {electricTruckModelInfo.filter(truck => truck.brand === selectedElectricBrand).map((truck, idx) => (
                    <option key={idx} value={truck.make}>{truck.make}</option>
                  ))}
                </select>
              </div>
              <ImageSlider images={electricTruckModelInfo} selectedTruck={selectedElectricTruck} onTruckChange={(id) => onTruckChange(id, true)} />
            </div>

            {/* Diesel Truck Section */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-xl font-nunito font-bold text-center text-gray-800 mb-4">Diesel Truck</h3>
              <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-3 items-center mb-4">
                <label className="text-sm font-nunito font-medium text-gray-700 text-left">Make:</label>
                <select
                  className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-nunito"
                  value={selectedDieselBrand}
                  onChange={handleDieselBrandChange}
                >
                  {uniqueDieselBrands.map((brand, idx) => (
                    <option key={idx} value={brand}>{brand}</option>
                  ))}
                </select>

                <label className="text-sm font-nunito font-medium text-gray-700 text-left">Model:</label>
                <select
                  className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-nunito"
                  value={selectedDieselMake}
                  onChange={handleDieselMakeChange}
                >
                  {dieselTruckModelInfo.filter(truck => truck.brand === selectedDieselBrand).map((truck, idx) => (
                    <option key={idx} value={truck.make}>{truck.make}</option>
                  ))}
                </select>
              </div>
              <ImageSlider images={dieselTruckModelInfo} selectedTruck={selectedDieselTruck} onTruckChange={(id) => onTruckChange(id, false)} />
            </div>
          </div>

          {/* Parameters Section */}
          <div className="space-y-6">
            {/* Financial Parameters */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <button
                  onClick={toggleFinancial}
                  className="flex items-center space-x-2 text-lg font-semibold text-gray-800 hover:text-gray-600 transition-colors duration-500 w-full text-left"
                >
                  <span className={`transform transition-transform duration-200 ${financialExpanded ? 'rotate-90' : ''}`}>
                    ▶
                  </span>
                  <span className="font-nunito">Financial Parameters</span>
                </button>
              </div>
              {financialExpanded && (
                <div className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <h4 className="font-medium font-nunito text-gray-700 border-b pb-2">Electric Financial Parameters</h4>
                      {electricParams.map((param) => (
                        (param.show && param.category === 'financial') && (
                          <InputGroup
                            type="number"
                            name={param.name}
                            key={param.name}
                            label={param.label}
                            description={param.description}
                            defaultValue={param.default}
                            value={param.cost}
                            onChange={(target) => handleParamChange('electric', param.name, target)}
                            evMsrpCost={evMsrpCost}
                          />
                        )
                      ))}
                    </div>
                    <div className="space-y-4">
                      <h4 className="font-medium font-nunito text-gray-700 border-b pb-2">Diesel Financial Parameters</h4>
                      {dieselParams.map((param) => (
                        (param.show && param.category === 'financial') && (
                          <InputGroup
                            type="number"
                            name={param.name}
                            key={param.name}
                            label={param.label}
                            description={param.description}
                            defaultValue={param.default}
                            value={param.cost}
                            onChange={(target) => handleParamChange('diesel', param.name, target)}
                          />
                        )
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Operational Parameters */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <button
                  onClick={toggleOperational}
                  className="flex items-center space-x-2 text-lg font-semibold text-gray-800 hover:text-gray-600 transition-colors duration-200 w-full text-left"
                >
                  <span className={`transform transition-transform duration-200 ${operationalExpanded ? 'rotate-90' : ''}`}>
                    ▶
                  </span>
                  <span className="font-nunito">Operational Parameters</span>
                </button>
              </div>
              {operationalExpanded && (
                <div className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <h4 className="font-medium font-nunito text-gray-700 border-b pb-2">Electric Operational Parameters</h4>
                      {electricParams.map((param) => (
                        (param.show && param.category === 'operational' && param.own === false) && (
                          <InputGroup
                            type="number"
                            name={param.name}
                            key={param.name}
                            label={param.label}
                            description={param.description}
                            defaultValue={param.default}
                            value={param.cost}
                            onChange={(target) => handleParamChange('electric', param.name, target)}
                          />
                        )
                      ))}
                    </div>
                    <div className="space-y-4">
                      <h4 className="font-medium font-nunito text-gray-700 border-b pb-2">Diesel Operational Parameters</h4>
                      {dieselParams.map((param) => (
                        (param.show && param.category === 'operational') && (
                          <InputGroup
                            type="number"
                            name={param.name}
                            key={param.name}
                            label={param.label}
                            description={param.description}
                            defaultValue={param.default}
                            value={param.cost}
                            onChange={(target) => handleParamChange('diesel', param.name, target)}
                          />
                        )
                      ))}
                    </div>
                  </div>

                  {/* Charger Selection */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
                    <div className="space-y-4">
                      <div className="border border-gray-300 rounded-lg p-4">
                        <legend className="text-base font-nunito font-medium text-gray-800 mb-4">
                          How do you plan to charge?
                        </legend>
                        <div className="space-y-3">
                          <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                              type="radio"
                              name="charger-option"
                              value="yes"
                              onChange={onChargerChange}
                              defaultChecked
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 focus:ring-2"
                            />
                            <span className="text-sm font-nunito text-gray-700">I plan to own my chargers</span>
                          </label>
                          <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                              type="radio"
                              name="charger-option"
                              value="no"
                              onChange={onChargerChange}
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 focus:ring-2"
                            />
                            <span className="text-sm font-nunito text-gray-700">I plan to use commercial charging stations</span>
                          </label>
                        </div>
                      </div>

                      {/* Conditional Charging Parameters */}
                      <div className="space-y-4">
                        {ownChargers === 'yes' ? electricParams.map((param) => (
                          (param.show && param.own && !param.thirdParty) && (
                            <InputGroup
                              type="number"
                              name={param.name}
                              key={param.name}
                              label={param.label}
                              description={param.description}
                              defaultValue={param.default}
                              value={param.cost}
                              onChange={(target) => handleParamChange('electric', param.name, target)}
                            />
                          ))
                        ) : electricParams.map((param) => (
                          (param.show && param.thirdParty && param.category === 'operational') && (
                            <InputGroup
                              type="number"
                              name={param.name}
                              key={param.name}
                              label={param.label}
                              description={param.description}
                              defaultValue={param.default}
                              value={param.cost}
                              onChange={(target) => handleParamChange('electric', param.name, target)}
                            />
                          )
                        ))}
                      </div>
                    </div>

                    {/* Port Operations */}
                    <div className="space-y-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h5 className="text-base font-nunito font-medium text-gray-800 mb-2">
                          Do you operate out of the Ports of Long Beach and Los Angeles?
                        </h5>
                        <p className="text-sm font-nunito text-gray-600 mb-2">
                          The Ports of Los Angeles and Long Beach charge $10 per 20-foot equivalent container for non-zero emission trucks.
                        </p>
                        <a
                          href="https://www.portoflosangeles.org/environment/air-quality/clean-truck-program"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-nunito text-blue-600 hover:text-blue-800 underline"
                        >
                          Read more
                        </a>
                      </div>

                      {dieselParams.map((param) => (
                        (param.ZEV) && (
                          <InputGroup
                            type="number"
                            name={param.name}
                            key={param.name}
                            label={param.label}
                            description={param.description}
                            defaultValue={param.default}
                            value={param.cost}
                            onChange={(target) => handleParamChange('diesel', param.name, target)}
                          />
                        )
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Reset Button */}
          <div className="text-center py-6">
            <ResetButton resetDefault={resetToDefault} />
          </div>
        </div>

        {/* TCO Display - Collapsible Sticky Footer */}
        <div className="sticky bottom-0 left-0 right-0 bg-white border-t-4 border-gray-200 shadow-lg z-50 transition-all duration-300 ease-in-out">
          {/* Collapse/Expand Button */}
          <div className="w-full flex justify-center bg-gray-50 border-b border-gray-200">
            <button
              onClick={() => setFooterCollapsed(!footerCollapsed)}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors duration-200 flex items-center space-x-2 font-nunito"
            >
              <span>{footerCollapsed ? 'Show TCO Results' : 'Hide TCO Results'}</span>
              <svg 
                className={`w-4 h-4 transform transition-transform duration-200 ${footerCollapsed ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {/* Collapsible Content */}
          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${footerCollapsed ? 'max-h-0' : 'max-h-96'}`}>
            <div className="w-full px-6 py-2">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-7xl mx-auto">
                <div className="text-center">
                  <p className="text-base font-bold text-gray-800 mb-2 font-nunito">
                    Electric TCO: ${Math.round(calculateTCO('electric', ownChargers)).toLocaleString()}
                  </p>
                  <div className="scale-75 origin-center">
                    <Piechart {...eProps} />
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-base font-bold text-gray-800 mb-2 font-nunito">
                    Diesel TCO: ${Math.round(calculateTCO('diesel', ownChargers)).toLocaleString()}
                  </p>
                  <div className="scale-75 origin-center">
                    <Piechart {...dProps} />
                  </div>
                </div>
              </div>
              <div className="text-center mt-2">
                <p className="text-xs text-gray-500 max-w-2xl mx-auto font-nunito">
                  TCO calculator for demonstration only. Results may vary. For detailed fleet analysis, see our upcoming Pro Calculator.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicCalculator;